// src/services/previews.js — live, DECRYPTED last-message previews for the chat list.
//
// Firestore's chat.lastMessage is only an E2EE placeholder ("🔒 Encrypted message"),
// so the list can't read it. Instead we subscribe to each chat's NEWEST message and
// decrypt it with this device's key — the row then shows the real text, live, exactly
// like the phone. One tiny listener per chat (limit 1), which is what WhatsApp Web does.

import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { decryptPerDeviceText, unsealMediaKey } from '../crypto/e2ee';
import { shortPlaceLabel } from './location';
import { timeLabel as voiceTimeLabel } from './voice';
import { flightNumberOf, cachedFlight, airlineName } from './flights';
import { hydrateReplyTo, replySnippet } from './replies';
import { hiddenIdsFor, isHiddenForMe, onHiddenChange } from './hiddenMessages';
import { previewRun, isSettledIn } from './mediaRuns';
import { settledFor, onRunsSettled } from './runSettled';

const TYPE_LABEL = {
  sticker: 'Sticker',
  gif: 'GIF', location: '📍 Location', contact: '👤 Contact', poll: '📊 Poll',
};
// used only when a media message has no thumbnail to show
const TYPE_FALLBACK = { image: '📷 Photo', images: '📷 Album', video: '🎥 Video' };

/** "2 Photos, 1 Video" — the album's real makeup, not the word "Album".
 *  An album's per-item kind is NOT in the message doc (the URLs are opaque `m/<hex>`
 *  keys with no extension); it only exists inside the sealed batch key payload, so we
 *  unseal it — a tiny nacl.box open against an already-cached device key. */
function albumLabel(kinds, n) {
  if (!kinds.length) return `${n} ${n === 1 ? 'item' : 'items'}`;
  const v = kinds.filter((k) => k === 'video').length;
  const p = kinds.length - v;
  const ph = `${p} ${p === 1 ? 'Photo' : 'Photos'}`;
  const vd = `${v} ${v === 1 ? 'Video' : 'Videos'}`;
  if (p && v) return `${ph}, ${vd}`;
  return v ? vd : ph;
}

/** Subscribe to previews for a set of chats.
 *  onPreview(chatId, { text, unreadable, senderId, type, ts, seen, delivered, sent, id }) */
export function watchChatPreviews(chatIds, myUid, onPreview, syncedFor, floorFor) {
  const unsubs = chatIds.map((chatId) => {
    /* 🟥 NOT limit(1). One document cannot answer "what is the newest message I
       can still see" — only "what is the newest document". Delete the newest of
       ten and the honest answer is the ninth, but with a window of one there is
       nothing else in the snapshot to fall back to, so the row either froze on
       the message you had just deleted or emptied a chat that was full. Twelve
       is enough to step over a run of votes, reactions and deletions and still
       be a single small read. */
    /* 👥 a group I was ADDED to: nothing from before my join (services/joinFloor) — the row showed the
          🔒 of a message sealed for the members of the time */
    const floor = typeof floorFor === 'function' ? Number(floorFor(chatId)) || 0 : 0;
    const q = floor > 0
      ? query(collection(db, 'chats', chatId, 'messages'), where('timestamp', '>', new Date(floor)), orderBy('timestamp', 'desc'), limit(12))
      : query(
      collection(db, 'chats', chatId, 'messages'),
      orderBy('timestamp', 'desc'),
      limit(12)
    );
    /* the newest snapshot, kept so a purely local change can be re-applied to it */
    let last = null;

    const run = async (snap) => {
      const hidden = hiddenIdsFor(chatId);
      /* 🟥 THE ROW THAT SAYS "hello", THEN AN OLDER MESSAGE, THEN "hello" AGAIN.
         A message this browser has just written carries `timestamp:
         serverTimestamp()`, and a bare `doc.data()` reports that as **null**
         until the server acknowledges it — the single-tick window exactly. The
         Firestore SDK still ranks the pending document first in this query
         (an unresolved server timestamp sorts after every real one), so the
         preview was computed from the right message with NO TIME on it. Handed
         to newerPreview with `ts: 0`, it lost to whatever the phone's export
         held, and the row quoted a message from before the photo for a second,
         until the ack landed and the clock came back.

         🟢 THE SAME ANSWER AS THE CHAT LIST'S SORT (services/chats.js): the
            pending stamp reads as this device's clock, which is what the row
            is claiming anyway — "you just said this". */
      const rows = snap.docs.map((doc) => ({ id: doc.id, ...doc.data({ serverTimestamps: 'estimate' }) }));
      const visible = rows.find((row) => !isHiddenForMe(row, myUid, hidden));

      /* 🟥 DID I DELETE THE NEWEST ONE? The row needs to know, because
         `chats/{id}.lastMessage` describes exactly that message — it is written
         by the sender and shared with the other person, so a delete-for-me
         cannot clear it. Without this flag the chat document quietly puts the
         deleted message back in the list.

         🟢 It is deliberately NOT the same question as `empty`. A chat with no
         documents at all is the normal state after the server's store-and-
         forward sweep, and there the chat doc and the phone's export are the
         only sources the row has — suppressing them would blank every synced
         chat on a fresh login. Emptiness and deletion look identical from
         `snap.docs.length` and mean opposite things. */
      const hidNewest = rows.length > 0 && isHiddenForMe(rows[0], myUid, hidden);

      /* 🟥 "NOTHING TO SHOW" HAS TO BE SAID OUT LOUD. This used to `return`, and
         a preview that is never emitted is indistinguishable from one that has
         not been computed yet — so the row kept whatever it had, which after a
         delete is the message you just deleted. `empty` is the flag that lets
         the row tell those two apart; see the note beside bodyText in Sidebar.

         The same applies to a chat with no documents at all, which is the state
         a brand-new chat and a fully-swept chat are both in. */
      if (!visible) {
        onPreview(chatId, { empty: true, hidNewest, text: '', unreadable: false, ts: null });
        return;
      }

      const m = visible;
      let text = '';
      /* set when the text is genuinely undecryptable — see the note below */
      let unreadable = false;
      // 🟢 REAL THUMBNAILS IN THE LIST. The blurred previews the phone ships on every
      //    media message (imageThumbnailBase64 / videoThumbnailBase64 / batchThumbnails)
      //    are already on THIS doc — no decrypt, no download, no extra request. So the
      //    row can show the actual picture instead of a 📷 glyph.
      let thumbs = null;
      let extra = 0;
      let icon = null;
      let route = null;
      let reply = null;

      /* 🟢 There is no `deletedFor` branch here any more, and that is the fix
         rather than an omission. It used to set `text = ''`, which the row read
         as "no preview yet" and answered by falling through to the phone's
         export and the chat doc — neither of which has heard of the deletion, so
         a message deleted for me came straight back as its own plaintext. Such a
         document is now skipped by isHiddenForMe before it ever gets here, and
         the NEXT visible message speaks for the row. */
      if (m.isDeleted || m.deleted) {
        text = 'Message deleted';
      } else if (m.type === 'text') {
        text = (await decryptPerDeviceText(m)) || '';
        // 🟥 BEFORE GIVING UP, ASK THE PHONE'S EXPORT. A message this browser has no
        //    sealed copy of is unreadable forever from Firestore — but history sync is
        //    holding the plaintext, and a row that says "🔒 Message" when the text is
        //    two objects away is just the list refusing to look.
        if (!text) {
          try {
            const arr = syncedFor?.(chatId);
            const hit = Array.isArray(arr)
              ? arr.find((x) => (x.id || x.clientTempId) === m.id)
              : null;
            const fromExport = hit?._plain
              || (hit?.text && !String(hit.text).startsWith('🔒') ? hit.text : '');
            if (fromExport) text = String(fromExport);
          } catch { /* fall through to the label */ }
        }
        /* 🟥 THE PADLOCK IS A FLAG, NOT A STRING IN `text`. This wrote the literal
           '🔒 Message' into the preview's text, and the chat row reads
           `preview.text || chatPreviewText(...) || …` — so a truthy padlock string
           won that expression outright and nothing after it could ever run.

           That is why "Fetching from your phone…" only appeared on Note to Self:
           it is the one chat with no message to fail to decrypt, so its preview
           text was empty and the fallback got a turn. Every other row already had
           a padlock in hand before the question was asked. (Sidebar.jsx has
           carried a comment claiming this was already a flag — it described an
           intention that was never implemented here.)

           `unreadable` says the same thing without occupying the field the row
           uses to decide whether it knows anything, which lets the row choose
           between "still arriving" and "arrived and cannot be read". */
        if (!text) unreadable = true;
        // 🟢 the app marks a link row with a glyph whether or not a preview resolved —
        //    a bare URL in the text is enough, which is why this tests both
        if (m.linkMetadata?.url || m.linkMetadata?.title || /https?:\/\/\S+/i.test(text)) icon = 'link';
      } else if ((m.type === 'image' || m.type === 'video') && (() => {
        /* 🧩 THE NEWEST PHOTO / VIDEO IS ONE OF A RUN THE CHAT DREW TOGETHER
           (services/mediaRuns rule 5) — so the row reads like an album: "2 Photos,
           1 Video" with their thumbnails. Only what the chat itself grouped. */
        const keys = settledFor(chatId);
        const from = rows.indexOf(m);
        const seq = rows.slice(from).filter((row) => !isHiddenForMe(row, myUid, hidden));
        const run = previewRun(seq, (x) => isSettledIn(keys, x));
        if (!run) return false;
        text = albumLabel(run.map((x) => (x.type === 'video' ? 'video' : 'image')), run.length);
        // a tile without a shipped preview draws blank — never the single "📷 Photo" glyph
        thumbs = run.slice(0, 4).map((x) => ({ b64: (x.type === 'video' ? x.videoThumbnailBase64 : x.imageThumbnailBase64) || null, video: x.type === 'video' }));
        extra = Math.max(0, run.length - 4);
        return true;
      })()) {
        /* the run branch above filled text / thumbs / extra */
      } else if (m.type === 'image') {
        text = 'Photo';
        if (m.imageThumbnailBase64) thumbs = [{ b64: m.imageThumbnailBase64 }];
      } else if (m.type === 'video') {
        text = 'Video';
        if (m.videoThumbnailBase64) thumbs = [{ b64: m.videoThumbnailBase64, video: true }];
      } else if (m.type === 'images') {
        const tiles = Array.isArray(m.batchThumbnails) ? m.batchThumbnails : [];
        const n = Array.isArray(m.images) ? m.images.length : tiles.length;
        let kinds = [];
        try {
          const keys = await unsealMediaKey(m.batchPerDeviceKeys, m.senderId, m.senderDeviceId);
          if (Array.isArray(keys)) kinds = keys.map((k) => (k?.type === 'video' ? 'video' : 'image'));
        } catch { /* label degrades to "N items" — never blocks the row */ }
        text = albumLabel(kinds, n);
        const shown = tiles.slice(0, 4);
        if (shown.some(Boolean)) thumbs = shown.map((b64, i) => ({ b64, video: kinds[i] === 'video' }));
        extra = Math.max(0, n - 4);
      } else if (m.type === 'shared_post') {
        /* 🟥 THIS ROW USED TO BE BLANK. `shared_post` fell through to the
           TYPE_LABEL table, which has no entry for it, so sharing a vibe into a
           chat left the list showing nothing at all — the chat jumped to the top
           with an empty line under its name.

           The words are the app's own (locales: `shared_post_preview` →
           "Shared a post"), so the phone and the browser read identically. The
           card is plaintext, so the post's picture is right there on the doc and
           the row can show it — no decrypt, no unseal, one <img>. */
        text = 'Shared a post';
        if (m.postImageUrl) thumbs = [{ url: m.postImageUrl, video: !!m.postIsVideo }];
      } else if (m.type === 'document') {
        icon = 'doc';
        text = m.documentName || 'Document';
      } else if (m.type === 'poll') {
        icon = 'poll';
        let q = null;
        try {
          const p = await unsealMediaKey(m.pollPerDeviceKeys, m.senderId, m.senderDeviceId);
          q = p?.question || null;
        } catch { /* fall back to the plain word */ }
        text = q || m.question || 'Poll';
      } else if (m.type === 'audio') {
        // 🟢 a mic glyph + the actual length, not "🎤 Voice message". `duration` is on the
        //    message doc in SECONDS, so this costs nothing — no decrypt, no fetch.
        icon = 'mic';
        const secs = Number(m.duration);
        text = Number.isFinite(secs) && secs > 0 ? voiceTimeLabel(secs) : 'Voice message';
      } else if (m.type === 'flight') {
        // 🟢 the row shows the ROUTE, not the word "Flight" — "DTW ✈ LAX" is the one
        //    thing you actually want to read at a glance, and it's what the app's
        //    ChatsTab draws too. Everything needed is plaintext on the doc, so this
        //    costs no decrypt and no network.
        icon = 'flight';
        const fd = m.flightData || cachedFlight(flightNumberOf(m) || '') || null;
        const from = fd?.origin?.code;
        const to = fd?.destination?.code;
        if (from && to && from !== '\u2014' && to !== '\u2014') {
          route = { from, to };
          text = `${from} \u2192 ${to}`;
        } else {
          // 🟢 no route yet (quota, or a card still resolving) — the flight number
          //    still says more than "Flight" does
          const num = flightNumberOf(m);
          const name = num ? airlineName(num.replace(/\d+$/, '')) : null;
          text = num ? [name, num].filter(Boolean).join(' ') : 'Flight';
        }
      } else if (m.type === 'location') {
        // 🟢 the row says WHERE, not "Location". Web-sent pins carry a name inside the
        //    sealed payload; the app's carry only coordinates, so those are reverse-
        //    geocoded once and cached.
        icon = 'pin';
        if (m.live) {
          text = 'Live location';
        } else {
          let label = null;
          try {
            const p = await unsealMediaKey(m.locationPerDeviceKeys, m.senderId, m.senderDeviceId);
            const lat = Number(p?.lat ?? p?.latitude);
            const lng = Number(p?.lng ?? p?.longitude);
            label = p?.name || null;
            if (!label && Number.isFinite(lat) && Number.isFinite(lng)) label = await shortPlaceLabel(lat, lng);
          } catch { /* fall through to the plain word */ }
          text = label || 'Location';
        }
      } else {
        text = TYPE_LABEL[m.type] || '';
      }
      // 🟢 A REPLY OUTRANKS ITS OWN CONTENT in the row, exactly like the app: what
      //    you want at a glance is "You replied to: “a poll”", not the reply's text.
      //    Note the snippet describes the QUOTED message, not this one — and since the
      //    quote arrives sealed, it gets unsealed here rather than rendering a padlock.
      if (m.replyTo && !m.isDeleted && !m.deleted) {
        const rt = (await hydrateReplyTo(m)) || m.replyTo;
        reply = { mine: m.senderId === myUid, snippet: replySnippet(rt) };
      }

      // no thumbnail to show -> keep the old glyph so the row still reads as media
      if (!thumbs && TYPE_FALLBACK[m.type]) text = TYPE_FALLBACK[m.type];

      onPreview(chatId, {
        id: m.id, empty: false, hidNewest,
        text, unreadable, thumbs, extra, icon, route, reply, senderId: m.senderId, type: m.type,
        ts: m.timestamp, seen: !!m.seen, delivered: !!m.delivered, sent: m.sent !== false,
        ...(m.seq != null ? { seq: m.seq } : {}),   // ✓✓ by the message's own seq (MessageTicks), as in the chat
      });
    };

    /* 🟢 run() is async and these are sync callbacks, so its rejection has
       nowhere to go — an unhandled rejection in a Firestore handler takes the
       listener down with it. A preview that fails is a row that keeps its old
       text; that is worth swallowing. */
    const unsub = onSnapshot(q, (snap) => {
      last = snap;
      run(snap).catch(() => {});
    }, () => {});
    /* 🟥 A LOCAL HIDE CHANGES NOTHING IN FIRESTORE, SO NOTHING RE-FIRES. When the
       message document has already been swept, "delete for me" is recorded only
       in this browser — deleteForMe returns false and ChatWindow hides the id.
       Without this the thread would drop the message and the row beside it would
       go on showing it, for the rest of the session. Re-running against the
       snapshot we already hold costs one pass over twelve documents. */
    const offHidden = onHiddenChange((changedChatId) => {
      if (changedChatId && changedChatId !== chatId) return;
      if (last) run(last).catch(() => {});
    });
    /* 🧩 …nor does the chat grouping photos it shows: re-read the same snapshot */
    const offRuns = onRunsSettled((changedChatId) => {
      if (changedChatId !== chatId) return;
      if (last) run(last).catch(() => {});
    });

    return () => { try { unsub(); } catch {} offHidden(); offRuns(); };
  });
  return () => unsubs.forEach((u) => { try { u(); } catch {} });
}
